import { Link } from 'react-router-dom';
import { adminApi } from '../../api/endpoints.js';
import { Button, Card, Grid3, Muted, Spinner, Tag, fmtDate, useAsync } from '../../ui.jsx';

const statusTone = {
  submitted: 'mid',
  under_review: '',
  offered: 'ok',
  rejected: 'no',
  withdrawn: ''
};

export default function AdminDashboard() {
  const { data, loading, error } = useAsync(() => adminApi.dashboard(), []);

  if (loading) return <Spinner label="Loading your dashboard" />;
  if (error) return <div className="note">We could not load your dashboard. Please try again.</div>;

  const { school, stats, revenue, recent } = data;

  const cards = [
    ['Applications', stats.applications, '/admin/applications'],
    ['Awaiting review', stats.awaitingReview, '/admin/applications?status=submitted'],
    ['Under review', stats.underReview, '/admin/applications?status=under_review'],
    ['Offers made', stats.offersMade, '/admin/applications?status=offered'],
    ['Rejected', stats.rejected, '/admin/applications?status=rejected'],
    ['Offers expiring', stats.offersExpiringSoon, '/admin/offers']
  ];

  return (
    <>
      <div className="row sp" style={{ marginBottom: 4 }}>
        <h2 style={{ margin: 0 }}>{school.name}</h2>
        <Tag tone={school.deadline ? 'ok' : 'mid'}>{school.statusLabel}</Tag>
      </div>
      <Muted>
        Deadline {fmtDate(school.deadline)}. Application fee GH{school.applicationFee ?? 'not set'}.
        {school.deadlineNote ? ` ${school.deadlineNote}` : ''}
      </Muted>

      <div className="stats">
        {cards.map(([label, value, to]) => (
          <Link key={label} to={to} className="stat" style={{ textDecoration: 'none', color: 'inherit' }}>
            <b>{value}</b>
            <span className="mu">{label}</span>
          </Link>
        ))}
        <div className="stat">
          <b>{stats.averageAggregate ?? '--'}</b>
          <span className="mu">Average aggregate</span>
        </div>
      </div>

      <Grid3>
        <Card>
          <h3 style={{ fontSize: 17, margin: '0 0 6px' }}>Application dates</h3>
          <Muted>Publish your deadline and reporting date. Entrance never sets them for you.</Muted>
          <div style={{ marginTop: 10 }}>
            <Link to="/admin/dates" className="btn sm" style={{ textDecoration: 'none' }}>Manage dates</Link>
          </div>
        </Card>
        <Card>
          <h3 style={{ fontSize: 17, margin: '0 0 6px' }}>Programmes and cut-offs</h3>
          <Muted>
            {stats.applications} applications against your published programme list and cut-offs.
          </Muted>
          <div style={{ marginTop: 10 }}>
            <Link to="/admin/programmes" className="btn sm" style={{ textDecoration: 'none' }}>Manage programmes</Link>
          </div>
        </Card>
        <Card>
          <h3 style={{ fontSize: 17, margin: '0 0 6px' }}>Confirmed payments</h3>
          <dl className="kv">
            <dt>Application fees</dt>
            <dd>GH{revenue.applicationFees}</dd>
            <dt>Service fees</dt>
            <dd>GH{revenue.serviceFees}</dd>
            <dt>Total</dt>
            <dd>GH{revenue.total}</dd>
          </dl>
        </Card>
      </Grid3>

      <Card>
        <div className="row sp" style={{ marginBottom: 6 }}>
          <b>Recent applications</b>
          <Link to="/admin/applications" className="lk">See all</Link>
        </div>
        {recent.length === 0 ? (
          <Muted>No applications have been submitted yet.</Muted>
        ) : (
          recent.map((item) => (
            <div className="prog" key={item.id}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <b>{item.name}</b>
                <div className="mu">{item.programmes.join(', ') || 'No programme chosen'}</div>
                <div className="mu">Submitted {fmtDate(item.submittedAt)}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <Tag tone={statusTone[item.status]}>{item.status.replace(/_/g, ' ')}</Tag>
                {!item.paid ? <div className="mu" style={{ marginTop: 4 }}>Not paid</div> : null}
                <div style={{ marginTop: 8 }}>
                  <Link to={`/admin/applications/${item.id}`} className="btn sm" style={{ textDecoration: 'none' }}>
                    Open
                  </Link>
                </div>
              </div>
            </div>
          ))
        )}
      </Card>

      <Card>
        <h3 style={{ fontSize: 17, margin: '0 0 8px' }}>Everything else</h3>
        <div className="row">
          <Link to="/admin/applications" className="btn s sm" style={{ textDecoration: 'none' }}>All applications</Link>
          <Link to="/admin/offers" className="btn s sm" style={{ textDecoration: 'none' }}>Offers</Link>
          <Link to="/admin/programmes" className="btn s sm" style={{ textDecoration: 'none' }}>Programmes</Link>
          <Link to="/admin/broadcast" className="btn s sm" style={{ textDecoration: 'none' }}>Message applicants</Link>
          <Link to="/admin/activity" className="btn s sm" style={{ textDecoration: 'none' }}>Change history</Link>
        </div>
      </Card>
    </>
  );
}
